import { useEffect, useRef } from 'react';

/**
 * Space 홀드 + 드래그 / 마우스 휠 클릭 + 드래그로 캔버스 패닝
 * DOM capture 단계에서 mousedown/mouseup을 가로채어 Fabric.js 간섭 완전 차단
 * Space 키의 브라우저 기본 스크롤도 컨테이너 레벨에서 차단
 */
const usePanning = (canvas, containerRef) => {
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const spaceHeldRef = useRef(false);

  useEffect(() => {
    if (!canvas) return;
    const upperEl = canvas.upperCanvasEl;
    if (!upperEl) return;
    const container = containerRef.current;

    const handleKeyDown = (e) => {
      if (e.key === ' ') {
        const activeEl = document.activeElement;
        if (activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA') return;
        e.preventDefault(); // 반복 이벤트 포함 모든 Space 스크롤 차단
        if (e.repeat || spaceHeldRef.current) return; // 초기화는 1회만
        spaceHeldRef.current = true;
        canvas.defaultCursor = 'grab';
        canvas.hoverCursor = 'grab';
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.renderAll();
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === ' ') {
        e.preventDefault();
        spaceHeldRef.current = false;
        isPanningRef.current = false;
        canvas.defaultCursor = 'default';
        canvas.hoverCursor = 'move';
        canvas.selection = true;
        canvas.skipTargetFind = false;
      }
    };

    // 컨테이너의 Space 키 스크롤 차단 (capture 단계)
    const blockSpaceScroll = (e) => {
      if (e.key === ' ' && spaceHeldRef.current) {
        e.preventDefault();
      }
    };

    // DOM capture 단계에서 가로채기 → Fabric.js에 도달 차단
    const handleMouseDown = (e) => {
      if (!spaceHeldRef.current && e.button !== 1) return;

      e.stopImmediatePropagation();
      e.preventDefault();

      isPanningRef.current = true;
      canvas.defaultCursor = 'grabbing';
      canvas.hoverCursor = 'grabbing';
      if (e.button === 1) {
        canvas.skipTargetFind = true;
        canvas.selection = false;
      }
      if (container) {
        panStartRef.current = {
          x: e.clientX, y: e.clientY,
          scrollLeft: container.scrollLeft, scrollTop: container.scrollTop,
        };
      }
    };

    const handleMouseMove = (e) => {
      if (!isPanningRef.current) return;
      if (!container) return;
      container.scrollLeft = panStartRef.current.scrollLeft - (e.clientX - panStartRef.current.x);
      container.scrollTop = panStartRef.current.scrollTop - (e.clientY - panStartRef.current.y);
    };

    const handleMouseUp = (e) => {
      if (!isPanningRef.current) return;

      e.stopImmediatePropagation();
      e.preventDefault();

      isPanningRef.current = false;
      if (spaceHeldRef.current) {
        canvas.defaultCursor = 'grab';
        canvas.hoverCursor = 'grab';
      } else {
        canvas.defaultCursor = 'default';
        canvas.hoverCursor = 'move';
        canvas.selection = true;
        canvas.skipTargetFind = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    // 컨테이너에서 Space 스크롤 차단 (capture 단계로 먼저 잡기)
    container?.addEventListener('keydown', blockSpaceScroll, true);
    upperEl.addEventListener('mousedown', handleMouseDown, true);
    upperEl.addEventListener('mouseup', handleMouseUp, true);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      container?.removeEventListener('keydown', blockSpaceScroll, true);
      upperEl.removeEventListener('mousedown', handleMouseDown, true);
      upperEl.removeEventListener('mouseup', handleMouseUp, true);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [canvas, containerRef]);

  return isPanningRef;
};

export default usePanning;
